import { PageInfoBar } from "@components/common/page-info-bar";
import { red } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Breakpoint } from "@mui/material";
import { MarkupsKpi } from "@pages/project-details/project-markups/markups-kpi";
import { markupsKPIsSelector } from "@store/markups/markups-selector";
import { useAppSelector } from "@store/store-helper";
import { useMemo } from "react";

/** Map of the number of items-per-row to show for each screen width breakpoint */
const customItemsPerRow: { [key in Breakpoint]: number } = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 6,
  xl: 6,
  xxl: 6,
};

/** Default height in pixels of the annotations KPI bar */
const DEFAULT_BAR_HEIGHT = 80;

interface Props {
  /** Whether the component skeleton animation should be shown */
  shouldShowSkeleton?: boolean;
}

/** Renders the KPI bar for the project markups (annotations) tab */
export function ProjectMarkupsDetails({
  shouldShowSkeleton = false,
}: Props): JSX.Element {
  const markupsKPIs = useAppSelector(markupsKPIsSelector);
  const { isScreenXs, isScreenSm, isScreenMd, isScreenLgOrLarger } =
    useMediaQueryList();

  /* eslint-disable @typescript-eslint/no-magic-numbers */
  /** The number of items-per-row that we want to show for each screen width breakpoint */
  const itemsPerRow = useMemo(() => {
    if (isScreenLgOrLarger) {
      return 4;
    }

    if (isScreenMd) {
      return 2;
    }

    if (isScreenSm) {
      return 2;
    }

    if (isScreenXs) {
      return 1;
    }
  }, [isScreenXs, isScreenMd, isScreenLgOrLarger, isScreenSm]);
  /* eslint-enable @typescript-eslint/no-magic-numbers */

  /** Whether the KPI item should be displayed in compact format (one row) */
  const isCompactFormat = useMemo(() => {
    return !isScreenLgOrLarger;
  }, [isScreenLgOrLarger]);

  if (shouldShowSkeleton) {
    return <PageInfoBar isLoading={true} />;
  }

  return (
    <PageInfoBar
      items={[
        {
          columnSize: itemsPerRow,
          content: (
            <MarkupsKpi
              label="Total annotations"
              value={markupsKPIs.total}
              isCompactFormat={isCompactFormat}
            />
          ),
        },
        {
          columnSize: itemsPerRow,
          content: (
            <MarkupsKpi
              label="Incomplete"
              value={
                markupsKPIs.toDo + markupsKPIs.inProgress + markupsKPIs.inReview
              }
              isCompactFormat={isCompactFormat}
            />
          ),
        },
        {
          columnSize: itemsPerRow,
          content: (
            <MarkupsKpi
              label="Overdue"
              value={markupsKPIs.overdue}
              valueSx={{
                color: red[500],
              }}
              isCompactFormat={isCompactFormat}
            />
          ),
        },
        {
          columnSize: itemsPerRow,
          content: (
            <MarkupsKpi
              label="Complete"
              value={markupsKPIs.resolved}
              isCompactFormat={isCompactFormat}
            />
          ),
        },
      ]}
      customItemsPerRow={customItemsPerRow}
      barHeight={isCompactFormat ? undefined : DEFAULT_BAR_HEIGHT}
    />
  );
}
